import { Link } from '@cambridgeassessment/cambridge-ui/build/components/Link';
import { Box, Typography } from '@material-ui/core';
import React, { useEffect, useRef, useState } from 'react';
import { hot } from 'react-hot-loader/root';
import { ICON_MAP } from './constants';
import { useStyles } from './styles';

type Props = {
  className?: string;
  dataTestId?: string;
  variant?: 'success' | 'warning' | 'error' | 'info';
  multiline?: boolean;
  title: string;
  description: string;
  link?: {
    text: string;
    href?: string;
    onClick?: () => void;
  };
};

export const countMaxContentWidth = (
  ref: React.RefObject<HTMLInputElement>
): number => {
  if (!ref.current) return null;

  const { width, maxWidth, overflow } = ref.current.style;

  ref.current.style.width = 'max-content';
  ref.current.style.maxWidth = 'max-content';
  ref.current.style.overflow = 'auto';

  const maxContentWidth = ref.current.scrollWidth;

  ref.current.style.width = width;
  ref.current.style.maxWidth = maxWidth;
  ref.current.style.overflow = overflow;

  return maxContentWidth;
};

export const countContainerWidth = (
  ref: React.RefObject<HTMLInputElement>
): number => {
  return ref.current?.offsetWidth;
};

const CustomNotification: React.FC<Props> = ({
  className,
  dataTestId = 'notification',
  variant = 'success',
  multiline = false,
  title,
  description,
  link,
}) => {
  const contentRef = useRef<HTMLInputElement>(null);
  const [maxContentWidth, setMaxContentWidth] = useState(null);
  const [containerWidth, setContainerWidth] = useState(null);

  const wrap = multiline || !!(maxContentWidth > containerWidth);

  const handleResize = () => {
    setContainerWidth(countContainerWidth(contentRef));
  };

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    setMaxContentWidth(countMaxContentWidth(contentRef));
    setContainerWidth(countContainerWidth(contentRef));
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const classes = useStyles({ variant, wrap });

  return (
    <Box
      className={`${classes.customNotification} ${className}`}
      data-testid={dataTestId}
    >
      <Box className={classes.icon} data-testid={`${variant}-icon`}>
        {ICON_MAP[variant]}
      </Box>
      <span
        ref={contentRef}
        className={classes.content}
        data-testid="notification-content"
      >
        <Typography
          variant="subtitle1"
          className={classes.title}
          data-testid="title"
        >
          {title}
        </Typography>
        <Typography
          variant="body1"
          className={classes.description}
          data-testid="description"
        >
          {description}
        </Typography>
        {link?.text && !link.onClick && (
          <Link
            href={link.href}
            underline="always"
            dataTestId={`${dataTestId}-link`}
          >
            {link.text}
          </Link>
        )}
        {link?.text && link.onClick && (
          <button
            data-testid={`${dataTestId}-link`}
            className={classes.linkButton}
            onClick={link.onClick}
          >
            {link.text}
          </button>
        )}
      </span>
    </Box>
  );
};

export default hot(CustomNotification);
